import { Link } from 'react-router-dom'
import { useAuth } from '@/lib/auth-context'
import { PERMISSIONS, hasPermission } from '@/lib/permissions'
import { useStore } from '@/lib/store'
import { Bell, Bug, Calendar, LogOut, Package, Settings } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { MenuSheet } from './MenuSheet'
import { openCannyFeedback } from '@/lib/canny'
import { config } from '@/lib/config'

export function TopBar() {
  const { user, logout } = useAuth()
  const notifications = useStore((state) => state.notifications)
  
  const unreadCount = notifications.filter((n) => !n.read).length

  const handleLogout = async () => {
    await logout()
  }

  // Display user info
  const displayName = user?.displayName || 'Utilisateur'
  const displayEmail = user?.email || ''
  const initials = displayName?.[0]?.toUpperCase() || 'U'

  const handleReportBug = () => {
    if (typeof window === 'undefined') return

    const url = window.location.href
    const path = window.location.pathname
    const projectMatch = path.match(/\/projets\/([^/]+)/)
    const projectId = projectMatch ? projectMatch[1] : null

    openCannyFeedback({
      page: path.startsWith('/dashboard') ? 'Dashboard' : path,
      url,
      projectId,
      userId: user?.userId || null,
      userEmail: user?.email || null,
      userName: user?.displayName || null,
    })
  }

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-background">
      <div className="flex h-16 items-center justify-between px-4 md:px-6">
        {/* Left side - Menu hamburger + Logo */}
        <div className="flex items-center gap-4">
          <MenuSheet />
          <Link to="/home" className="flex items-center">
            <span className="text-xl font-bold text-primary">SOLY</span>
          </Link>
        </div>

        {/* Right side - Commandes + Planning + Bug report + Notifications + Avatar */}
        <div className="flex items-center gap-4">
          {/* The two daily business surfaces, kept adjacent so the boundary with the utility
              controls (bug / bell / avatar) stays readable.

              Both are gated the same way: the org has the module (Features:Planning) AND this user
              is allowed in (SOLY-PERM-001). A shortcut to a page that redirects is worse than no
              shortcut. They are HIDDEN rather than greyed out — the doctrine in `permissions.ts`
              greys a gesture and hides a page.

              `size-11` overrides shadcn's `size="icon"` (`size-9` = 36px), eight under the 44px
              touch minimum. The product supports tablets (DESIGN-CONTEXT §3.3) and these are two
              adjacent targets 16px apart leading to similar-looking destinations. It has to be
              `size-11` and not `h-11 w-11`: tailwind-merge collapses the former against `size-9`
              but keeps BOTH of the latter, leaving the winner to stylesheet emit order. */}
          {user?.features?.planning && hasPermission(user, PERMISSIONS.ORDERS_READ) && (
            <Button
              asChild
              variant="ghost"
              size="icon"
              className="size-11"
              /* « Toutes » separates this ORG-WIDE registry from the per-project one at
                 /planning/projects/:id/commandes — both exist, and the icon alone cannot say which. */
              aria-label="Toutes les commandes fournisseurs"
              title="Toutes les commandes fournisseurs"
              data-testid="topbar-supplier-orders-btn"
            >
              <Link to="/supplier-orders">
                <Package className="h-5 w-5" />
              </Link>
            </Button>
          )}
          {user?.features?.planning && hasPermission(user, PERMISSIONS.PLANNING_READ) && (
            <Button asChild variant="ghost" size="icon" className="size-11" aria-label="Planning" title="Planning" data-testid="topbar-planning-btn">
              <Link to="/planning">
                <Calendar className="h-5 w-5" />
              </Link>
            </Button>
          )}
          {/* Report Bug button */}
          {config.cannyAppId && (
            <>
              <Button
                variant="outline"
                size="sm"
                className="gap-1 hidden md:flex"
                onClick={handleReportBug}
              >
                <Bug className="h-4 w-4" />
                <span>Signaler un bug</span>
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                onClick={handleReportBug}
                aria-label="Signaler un bug"
              >
                <Bug className="h-5 w-5" />
              </Button>
            </>
          )}
          {/* Notifications - Non functional for MVP */}
          <Button 
            variant="ghost" 
            size="icon" 
            className="relative" 
            disabled
            aria-label="Notifications"
          >
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-destructive" />
            )}
          </Button>

          {/* User dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="topbar-user-menu-btn" variant="ghost" className="gap-2">
                <Avatar className="h-10 w-10">
                  <AvatarFallback>{initials}</AvatarFallback>
                </Avatar>
                <span className="hidden md:inline-block max-w-[120px] truncate">
                  {displayName}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[240px]">
              <DropdownMenuLabel>
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-medium">{displayName}</p>
                  {displayEmail && (
                    <p className="text-xs text-muted-foreground">{displayEmail}</p>
                  )}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/profil" className="cursor-pointer">
                  <Settings className="mr-2 h-4 w-4" />
                  Mon profil
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="topbar-logout-btn" onClick={handleLogout} className="cursor-pointer">
                <LogOut className="mr-2 h-4 w-4" />
                Se déconnecter
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  )
}
